import { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp, TrendingDown, Activity, BarChart3, Target, Clock,
  Loader2, AlertTriangle, RefreshCw, CheckCircle, XCircle, Minus, Info,
} from 'lucide-react';
import { Spinner, InfoTip } from '../components/ui';
import {
  getForecastAccuracySummary,
  getForecastAccuracyRecent,
} from '../services/api';

const WINDOW_OPTIONS = [
  { id: 7,   label: '7 days' },
  { id: 30,  label: '30 days' },
  { id: 90,  label: '90 days' },
  { id: 365, label: '1 year' },
];

const HORIZON_ORDER = ['1h', '4h', '24h'];

function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

function fmtPct(v, dp = 1) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(dp)}%`;
}

function fmtPrice(v) {
  if (!Number.isFinite(v)) return '—';
  if (v >= 1) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return `$${v.toPrecision(4)}`;
}

function accuracyColor(accuracy, decided) {
  if (accuracy == null) return 'text-gray-500';
  if (decided < 10) return 'text-amber-400';   // low sample size = caveat
  if (accuracy >= 60) return 'text-emerald-400';
  if (accuracy >= 50) return 'text-blue-400';
  if (accuracy >= 40) return 'text-amber-400';
  return 'text-red-400';
}

function OutcomeIcon({ outcome }) {
  if (outcome === 'hit')     return <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />;
  if (outcome === 'miss')    return <XCircle className="w-3.5 h-3.5 text-red-400" />;
  return <Minus className="w-3.5 h-3.5 text-gray-500" />;
}

// ─── Global accuracy tile ────────────────────────────────────────────────────

function HorizonTile({ horizon, bucket }) {
  const decided = bucket?.decided ?? 0;
  const accuracy = bucket?.accuracy;
  const color = accuracyColor(accuracy, decided);
  return (
    <Card>
      <div className="flex items-center gap-2 mb-2">
        <Clock className="w-4 h-4 text-gray-500" />
        <p className="text-xs uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1.5">
          {horizon} horizon
          <InfoTip side="bottom">Headline accuracy across all symbols at the {horizon} forecast horizon. Neutral moves (&lt;0.1%) are excluded. Color: green ≥60%, blue 50-60%, amber 40-50%, red &lt;40%. Amber overlay when sample size is too small to trust.</InfoTip>
        </p>
      </div>
      <div className="flex items-baseline gap-3">
        <p className={`text-4xl font-black ${color}`}>{fmtPct(accuracy)}</p>
        <p className="text-xs text-gray-500">accuracy</p>
      </div>
      <p className="text-[11px] text-gray-500 mt-1">
        {decided > 0
          ? `${bucket.hits}/${decided} decided · ${bucket.neutral || 0} neutral`
          : 'no resolved forecasts yet'}
      </p>
      {bucket?.ci95 && decided > 0 && (
        <p className="text-[10px] text-gray-600 mt-1 flex items-center gap-1.5">
          <span>95% CI: <span className="font-mono">{bucket.ci95.low.toFixed(1)}% – {bucket.ci95.high.toFixed(1)}%</span></span>
          <InfoTip side="top">Wilson 95% confidence interval. Wider = smaller sample = trust the headline less. Don't read too much into any single number until sample size n ≥ 30.</InfoTip>
        </p>
      )}
    </Card>
  );
}

// ─── Per-asset breakdown table ───────────────────────────────────────────────

function PerAssetTable({ buckets }) {
  const grouped = useMemo(() => {
    const map = {};
    for (const b of buckets) {
      if (!map[b.symbol]) map[b.symbol] = {};
      map[b.symbol][b.horizon] = b;
    }
    return map;
  }, [buckets]);

  const symbols = Object.keys(grouped).sort();
  if (symbols.length === 0) {
    return (
      <Card className="text-center py-12">
        <BarChart3 className="w-8 h-8 text-gray-600 mx-auto mb-2" />
        <p className="text-sm text-gray-400">No per-asset data yet.</p>
        <p className="text-xs text-gray-600 mt-1">As forecasts are emitted on different symbols and resolved, they show up here.</p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Per-asset accuracy</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] text-gray-500 uppercase tracking-wider border-b border-dark-700/50">
              <th className="text-left py-2 px-3">Symbol</th>
              {HORIZON_ORDER.map(h => (
                <th key={h} className="text-right py-2 px-3">{h}</th>
              ))}
              <th className="text-right py-2 px-3">Total decided</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-700/30">
            {symbols.map(sym => {
              const totalDecided = HORIZON_ORDER.reduce((s, h) => s + (grouped[sym][h]?.decided || 0), 0);
              return (
                <tr key={sym} className="hover:bg-dark-700/20">
                  <td className="py-2.5 px-3 font-mono text-white text-xs">{sym}</td>
                  {HORIZON_ORDER.map(h => {
                    const b = grouped[sym][h];
                    if (!b || !b.decided) return <td key={h} className="py-2.5 px-3 text-right text-gray-600 text-xs">—</td>;
                    const color = accuracyColor(b.accuracy, b.decided);
                    return (
                      <td key={h} className="py-2.5 px-3 text-right">
                        <span className={`font-bold text-xs ${color}`}>{fmtPct(b.accuracy)}</span>
                        <span className="text-[10px] text-gray-600 ml-1.5">n={b.decided}</span>
                      </td>
                    );
                  })}
                  <td className="py-2.5 px-3 text-right text-gray-500 text-xs font-mono">{totalDecided}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Recent forecasts table (transparency) ───────────────────────────────────

function RecentForecastsTable({ items, loading }) {
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Recent forecasts (resolved)</h3>
        <p className="text-[10px] text-gray-600">Click any row for full transparency — every prediction is traceable to real OHLCV.</p>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">No resolved forecasts yet — the resolver runs every 15 min.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] text-gray-500 uppercase tracking-wider border-b border-dark-700/50">
                <th className="text-left py-2 px-2">Resolved</th>
                <th className="text-left py-2 px-2">Symbol</th>
                <th className="text-center py-2 px-2">Horizon</th>
                <th className="text-right py-2 px-2">Captured</th>
                <th className="text-right py-2 px-2">Resolved</th>
                <th className="text-right py-2 px-2">Actual</th>
                <th className="text-center py-2 px-2">Predicted</th>
                <th className="text-center py-2 px-2">Outcome</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700/30">
              {items.map((r) => {
                const dirIcon = r.predictedDirection === 'up'
                  ? <TrendingUp className="w-3 h-3 text-emerald-400 inline" />
                  : <TrendingDown className="w-3 h-3 text-red-400 inline" />;
                const actualColor = r.actualChangePct >= 0 ? 'text-emerald-400' : 'text-red-400';
                return (
                  <tr key={r.forecastId} className="hover:bg-dark-700/20">
                    <td className="py-2 px-2 text-gray-500">{new Date(r.resolvedAt).toLocaleString()}</td>
                    <td className="py-2 px-2 font-mono text-white">{r.symbol}</td>
                    <td className="py-2 px-2 text-center text-gray-400">{r.horizon}</td>
                    <td className="py-2 px-2 text-right text-gray-400 font-mono">{fmtPrice(r.capturedPrice)}</td>
                    <td className="py-2 px-2 text-right text-gray-400 font-mono">{fmtPrice(r.resolvedPrice)}</td>
                    <td className={`py-2 px-2 text-right font-mono ${actualColor}`}>
                      {r.actualChangePct >= 0 ? '+' : ''}{r.actualChangePct?.toFixed(2)}%
                    </td>
                    <td className="py-2 px-2 text-center">{dirIcon} <span className="text-gray-500">{r.probUp}%</span></td>
                    <td className="py-2 px-2 text-center"><OutcomeIcon outcome={r.outcome} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────

export default function ForecastAccuracyPage() {
  const [windowDays, setWindowDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentLoading, setRecentLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load(days) {
    setLoading(true);
    setError(null);
    try {
      const res = await getForecastAccuracySummary(days);
      setSummary(res?.data || res);
    } catch (err) {
      setError(err.message || 'Could not load accuracy data');
    }
    setLoading(false);
  }

  async function loadRecent() {
    setRecentLoading(true);
    try {
      const res = await getForecastAccuracyRecent({ days: 7, limit: 50 });
      setRecent(res?.data?.recent || res?.recent || []);
    } catch {}
    setRecentLoading(false);
  }

  useEffect(() => { load(windowDays); }, [windowDays]);
  useEffect(() => { loadRecent(); }, []);

  const globalByHorizon = useMemo(() => {
    if (!summary?.global) return {};
    const map = {};
    for (const b of summary.global) map[b.horizon] = b;
    return map;
  }, [summary]);

  const totalDecided = useMemo(() => {
    if (!summary?.global) return 0;
    return summary.global.reduce((s, b) => s + (b.decided || 0), 0);
  }, [summary]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-2">
            <Target className="w-6 h-6" /> Forecast Accuracy Track Record
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
              VERIFIED
            </span>
          </h1>
          <p className="text-gray-400 text-sm mt-1 max-w-3xl leading-relaxed">
            Every probability the haythix forecast engine emits gets logged with the spot price at capture time.
            A worker resolves each forecast against real OHLCV at horizon. <strong className="text-emerald-400">Every number on this page comes from real history</strong> — no backfills, no marketing claims.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={windowDays} onChange={e => setWindowDays(Number(e.target.value))}
            className="bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-accent-primary focus:outline-none">
            {WINDOW_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <button onClick={() => { load(windowDays); loadRecent(); }}
            className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-dark-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Honesty banner — explain caveats up front */}
      <Card className="!p-4 border-emerald-500/20 bg-emerald-500/5">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-300">How this is computed (read this first)</p>
            <ul className="text-xs text-emerald-300/80 mt-1 space-y-0.5 list-disc list-inside">
              <li>Hits = predicted direction matched the actual move at horizon. Misses = opposite. Neutrals = move &lt;0.1% in either direction (excluded from accuracy %).</li>
              <li>Resolution price = close of the candle nearest the horizon moment, fetched from real exchange OHLCV.</li>
              <li>Wilson 95% CI is shown — wider intervals = smaller sample size → don't read too much into any single number until n ≥ 30.</li>
              <li>If the resolver can't fetch real data at horizon, the forecast stays unresolved — never marked hit/miss without real numbers.</li>
            </ul>
          </div>
        </div>
      </Card>

      {error && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Spinner className="text-accent-primary" /></div>
      ) : totalDecided === 0 ? (
        <Card className="text-center py-16">
          <Activity className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <p className="text-sm font-semibold text-amber-300">Track record is just starting</p>
          <p className="text-xs text-gray-500 mt-2 max-w-md mx-auto">
            No forecasts have been resolved yet in the {windowDays}-day window. As users hit the forecast page on real-data pairs (BTC/USDT, ETH/USDT, etc.), entries get logged
            and the resolver picks them up at horizon. Check back tomorrow.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {HORIZON_ORDER.map(h => (
              <HorizonTile key={h} horizon={h} bucket={globalByHorizon[h]} />
            ))}
          </div>

          <PerAssetTable buckets={summary.perAsset || []} />
        </>
      )}

      <RecentForecastsTable items={recent} loading={recentLoading} />

      <p className="text-[10px] text-gray-600 text-center">
        Public endpoint:&nbsp;
        <code className="font-mono text-gray-500">GET /api/forecast-accuracy/summary?days={windowDays}</code>
        &nbsp;— anyone can verify these numbers independently.
      </p>
    </div>
  );
}
